import { useMemo, useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useMaintenanceWindows, useDeleteMaintenanceWindow, useUpdateMaintenanceWindow } from '../../hooks/useMaintenance';
import { useServices } from '../../hooks/useServices';
import { useGroups } from '../../hooks/useGroups';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Modal } from '../common/Modal';
import { Skeleton } from '../common/Skeleton';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { describeTargets } from '../notifications/TargetPicker';
import { MaintenanceForm } from './MaintenanceForm';
import { MaintenanceWindow } from '../../types';

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon, Wed 02:00–04:00 Europe/London" or "13 Sep 2026, 22:00 → 14 Sep 2026, 01:00". */
export function describeSchedule(w: MaintenanceWindow): string {
  if (w.recurrence) {
    const days = [...w.recurrence.weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map(d => DAY[d]);
    const dayText = days.length === 7 ? 'Every day' : days.join(', ');
    const span = w.recurrence.end_time < w.recurrence.start_time ? ' (overnight)' : '';
    return `${dayText} ${w.recurrence.start_time}–${w.recurrence.end_time}${span} · ${w.timezone}`;
  }
  return `${formatDateTime(w.start_at)} → ${formatDateTime(w.end_at)}`;
}

function isPast(w: MaintenanceWindow): boolean {
  if (w.recurrence || !w.end_at) return false;
  const end = new Date(w.end_at.replace(' ', 'T') + 'Z');
  return end.getTime() < Date.now();
}

export function MaintenancePage() {
  useDocumentTitle('Maintenance');
  const { data: windows, isLoading } = useMaintenanceWindows();
  const { data: services } = useServices();
  const { data: groups } = useGroups();
  const remove = useDeleteMaintenanceWindow();
  const update = useUpdateMaintenanceWindow();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('services.edit');

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<MaintenanceWindow | null>(null);
  const [deleting, setDeleting] = useState<MaintenanceWindow | null>(null);

  const lookup = useMemo(() => ({
    services: new Map((services ?? []).map(s => [s.id, s.name])),
    groups: new Map((groups ?? []).map(g => [g.id, g.name])),
  }), [services, groups]);

  // Active first, then upcoming/recurring, then finished one-offs.
  const sorted = useMemo(() => {
    const rank = (w: MaintenanceWindow) => (w.active ? 0 : isPast(w) ? 2 : 1);
    return [...(windows ?? [])].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [windows]);

  const toggleEnabled = (w: MaintenanceWindow) => {
    update.mutate({ id: w.id, data: { enabled: !w.enabled } }, {
      onSuccess: () => addToast('success', w.enabled ? `Disabled "${w.name}"` : `Enabled "${w.name}"`),
      onError: err => addToast('error', apiError(err, 'Could not update the window')),
    });
  };

  const handleDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => { addToast('success', `Deleted "${deleting.name}"`); setDeleting(null); },
      onError: err => { addToast('error', apiError(err, 'Failed to delete the window')); setDeleting(null); },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Maintenance</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Planned downtime. Services in a window show as <span className="font-medium">Maintenance</span>, send no alerts, and the time is excluded from uptime.
          </p>
        </div>
        {canEdit && <Button onClick={() => setShowCreate(true)}>Add window</Button>}
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} className="p-4 space-y-2">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-3 w-2/3" />
            </Card>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No maintenance windows.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Declare one before a deploy or a backup so it does not page anyone.
          </p>
          {canEdit && <Button className="mt-4" onClick={() => setShowCreate(true)}>Add your first window</Button>}
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(w => {
            const past = isPast(w);
            return (
              <Card key={w.id} className={`p-4 ${w.active ? 'border-l-[3px] border-l-status-maintenance' : ''} ${past || !w.enabled ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-semibold">{w.name}</h2>
                      {w.active && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300">
                          Active now
                        </span>
                      )}
                      {!w.enabled && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          Disabled
                        </span>
                      )}
                      {past && w.enabled && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          Finished
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400">{w.recurrence ? 'Recurring' : 'One-off'}</span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 tabular-nums">{describeSchedule(w)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{describeTargets(w.targets, lookup)}</p>
                    {w.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{w.description}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button variant="secondary" size="sm" loading={update.isPending && update.variables?.id === w.id} onClick={() => toggleEnabled(w)}>
                        {w.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(w)}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(w)}>Delete</Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add maintenance window">
        {showCreate && <MaintenanceForm onClose={() => setShowCreate(false)} />}
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit maintenance window">
        {editing && <MaintenanceForm key={editing.id} window={editing} onClose={() => setEditing(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Delete maintenance window"
        message={`"${deleting?.name}" will be removed${deleting?.active ? ' and its services will resume alerting immediately' : ''}.`}
        confirmLabel="Delete window"
        loading={remove.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
